import { useState, useCallback } from 'react';

interface ReturnLinkHintProps {
  url: string;
  note?: string;
}

export default function ReturnLinkHint({ url, note }: ReturnLinkHintProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [url]);

  return (
    <div className="rounded-lg border border-dashed border-white/20 px-4 py-3">
      <div className="flex items-center gap-2 mb-2">
        <svg
          className="w-4 h-4 text-slate-400 flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z"
          />
        </svg>
        <span className="text-xs font-medium text-slate-400">
          Your return link
        </span>
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          readOnly
          value={url}
          className="flex-1 min-w-0 px-2.5 py-1.5 text-xs bg-white/5 border border-white/10 rounded-lg text-slate-300 select-all"
          onFocus={(e) => e.target.select()}
        />
        <button
          onClick={handleCopy}
          className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
            copied
              ? 'bg-green-500 text-white'
              : 'bg-white/5 text-slate-300 hover:bg-white/10'
          }`}
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      {note && (
        <p className="mt-2 text-xs text-slate-400">{note}</p>
      )}
    </div>
  );
}
